import { ORDER_STATUS_FLOW, type OrderStatusValue } from '@giftsay/constants';

import type { BaseEntity } from './entity.js';
import type { PaymentProvider } from './payment-provider.js';

/**
 * STORE is the only entityType with real behavior today. FRANCHISE is
 * reserved on the type so onboarding/UI can start referencing it, but no
 * franchise-specific logic (shared catalog, multi-outlet scoping, etc.)
 * exists yet — treat it as scope-only until that's built.
 */
export const BUSINESS_TYPES = {
  STORE: 'STORE',
  FRANCHISE: 'FRANCHISE',
} as const;
export type BusinessType = (typeof BUSINESS_TYPES)[keyof typeof BUSINESS_TYPES];

export const STORE_STATUSES = {
  PENDING: 'pending',
  ACTIVE: 'active',
  SUSPENDED: 'suspended',
} as const;
export type StoreStatus = (typeof STORE_STATUSES)[keyof typeof STORE_STATUSES];

export const KYC_STATUSES = {
  PENDING: 'pending',
  APPROVED: 'approved',
  REJECTED: 'rejected',
} as const;
export type KycStatus = (typeof KYC_STATUSES)[keyof typeof KYC_STATUSES];

export const LEGAL_BUSINESS_TYPES = {
  INDIVIDUAL: 'individual',
  PROPRIETORSHIP: 'proprietorship',
  PARTNERSHIP: 'partnership',
  PRIVATE_LIMITED: 'private_limited',
} as const;
export type LegalBusinessType = (typeof LEGAL_BUSINESS_TYPES)[keyof typeof LEGAL_BUSINESS_TYPES];

/**
 * Public-safe tenant document — anything the storefront needs to render
 * pre-auth (name, branding, whether the store is live) lives here so it can
 * be read without exposing `StoreBusinessDetails`.
 */
export interface Store extends BaseEntity {
  entityType: BusinessType;
  name: string;
  /** Subdomain segment, e.g. "acme" -> acme.giftsay.in. Globally unique, lowercase. */
  slug: string;
  status: StoreStatus;
  kycStatus: KycStatus;
  theme: {
    /** Shown in the storefront header and footer; null falls back to the store's name. */
    logoUrl: string | null;
    primaryColor: string | null;
    // The fields below are absent on stores saved before they existed — read them as null.
    /** One line under the footer logo, e.g. "Say it with a Gift." */
    tagline?: string | null;
    /** Square browser-tab icon; null falls back to the logo. */
    faviconUrl?: string | null;
    /** The storefront home page's browser title / search-result title. */
    seoTitle?: string | null;
    /** The storefront's default meta description for search results and link previews. */
    seoDescription?: string | null;
    /** The coloured strip above the storefront header; null hides it. Absent = never saved. */
    announcementText?: string | null;
  };
  domain: {
    customDomain: string | null;
    verified: boolean;
  };
  /** Platform-controlled (admin only, never the vendor). Absent on stores saved before it
   *  existed — read it through `resolveStoreNotifications`. */
  notifications?: StoreNotificationSettings;
  /** Platform-controlled storefront features (admin only). Absent on stores saved before it
   *  existed — read it through `resolveStoreFeatures`. */
  features?: StoreFeatureSettings;
  /** Fulfilment settings the vendor (or staff) edit from Settings › Shipping. Absent on stores
   *  saved before it existed — read it through `resolveStoreShipping`. */
  shipping?: StoreShippingSettings;
  /** Platform-controlled (admin only): which gateway the store's online payments go through.
   *  Absent on stores saved before it existed — read it through `resolveStorePayments`. */
  payments?: StorePaymentSettings;
  /** Platform-controlled (admin only): extra details the storefront's checkout asks for, e.g.
   *  "GST number". Absent/empty = the checkout shows none. */
  checkoutFields?: StoreCheckoutField[];
  /** Platform-controlled (admin only): extra charges added to the store's orders, e.g. a
   *  service charge. Absent/empty = none. */
  charges?: StoreCharge[];
}

export const STORE_CHARGE_TYPES = {
  FIXED: 'fixed',
  PERCENTAGE: 'percentage',
} as const;
export type StoreChargeType = (typeof STORE_CHARGE_TYPES)[keyof typeof STORE_CHARGE_TYPES];

/** An extra line added to a store's orders at checkout, e.g. "Service charge ₹5 on orders of
 *  ₹1,000 or more". Both the threshold and a percentage use the cart subtotal (before discounts),
 *  the same base as coupon minimums and the free-shipping threshold. */
export interface StoreCharge {
  id: string;
  /** Shown to the customer, e.g. "Service charge". */
  name: string;
  type: StoreChargeType;
  /** `fixed`: minor units (paise). `percentage`: percent of the subtotal, e.g. 2.5. */
  value: number;
  /** Applies only when the subtotal is at least this (paise). 0 = every order. */
  minOrderAmount: number;
  isActive: boolean;
}

export const MAX_STORE_CHARGES = 10;
export const STORE_CHARGE_NAME_MAX = 40;

/** One extra question on a store's checkout — answers are snapshotted onto the order as
 *  `OrderAdditionalInfo`. */
export interface StoreCheckoutField {
  /** Stable key the customer's answer is sent under; survives the label being renamed. */
  id: string;
  /** e.g. "GST number". */
  label: string;
  placeholder: string | null;
  /** The customer can't place an order without answering it. */
  required: boolean;
}

export const MAX_STORE_CHECKOUT_FIELDS = 10;
export const CHECKOUT_FIELD_LABEL_MAX = 60;
export const CHECKOUT_FIELD_VALUE_MAX = 200;
export const ORDER_INSTRUCTIONS_MAX = 500;

export interface StorePaymentSettings {
  provider: PaymentProvider;
  /** The store's own gateway accounts. The secret values live in Secret Manager
   *  (`store-<storeId>-<provider>`); only these non-secret hints are kept on the store. A
   *  provider without an entry uses the platform's default account. */
  credentials?: Partial<Record<PaymentProvider, StoreGatewayCredentialInfo>>;
}

/** Which of the platform's or the store's own gateway account a payment went through — refunds
 *  and webhook checks must use the same one. */
export type GatewayAccount = 'platform' | 'store';

export interface StoreGatewayCredentialInfo {
  /** Safe to show: Razorpay's key id (public), or Cashfree's App ID with the middle masked. */
  publicIdHint: string;
  /** Cashfree only. */
  environment: 'sandbox' | 'production' | null;
  /** Razorpay only: whether a webhook secret was saved with the keys. */
  hasWebhookSecret: boolean;
  updatedAt: string;
  updatedBy: string;
}

/** Razorpay was the only gateway before per-store selection existed, so it stays the default. */
export const DEFAULT_STORE_PAYMENT_SETTINGS: StorePaymentSettings = {
  provider: 'razorpay',
};

export function resolveStorePayments(
  store: Pick<Store, 'payments'> | null | undefined,
): StorePaymentSettings {
  return { ...DEFAULT_STORE_PAYMENT_SETTINGS, ...store?.payments };
}

/** Which order notifications a store gets — admins switch these off for stores that don't
 *  need them, to cut email and Firestore cost. */
export interface StoreNotificationSettings {
  /** Order lifecycle emails to the store's customers (placed, confirmed, shipped, ...). */
  orderEmails: boolean;
  /** The vendor portal's live new-order bell. */
  orderInApp: boolean;
}

export const DEFAULT_STORE_NOTIFICATION_SETTINGS: StoreNotificationSettings = {
  orderEmails: true,
  orderInApp: true,
};

export function resolveStoreNotifications(
  store: Pick<Store, 'notifications'> | null | undefined,
): StoreNotificationSettings {
  return { ...DEFAULT_STORE_NOTIFICATION_SETTINGS, ...store?.notifications };
}

/** Storefront features an admin switches on or off per store. */
export interface StoreFeatureSettings {
  /** The "For bulk orders" WhatsApp button on product pages. */
  bulkOrderWhatsApp: boolean;
  /** Vendor home page: order counts by status (total, pending, to fulfil, shipped, delivered,
   *  cancelled). Cheap — counted with aggregation queries. Platform staff always see everything. */
  basicDashboard: boolean;
  /** Vendor home page: the full sales dashboard (order value, delivery charges, refunds, payments,
   *  daily chart, top products) — includes the basic counts. Each view reads every order in range. */
  advancedDashboard: boolean;
  /** Product videos: uploading one in the product form (admin and vendor portal alike) and
   *  showing it in the storefront gallery. Off keeps any uploaded video, just hidden. */
  productVideo: boolean;
  /** Customers may cancel their own order on the storefront — only while it's still pending
   *  (not yet confirmed). Off hides the button and the API refuses customer cancellations. */
  customerCancel: boolean;
}

export const DEFAULT_STORE_FEATURE_SETTINGS: StoreFeatureSettings = {
  bulkOrderWhatsApp: true,
  basicDashboard: false,
  advancedDashboard: false,
  productVideo: false,
  customerCancel: false,
};

export function resolveStoreFeatures(
  store: Pick<Store, 'features'> | null | undefined,
): StoreFeatureSettings {
  return { ...DEFAULT_STORE_FEATURE_SETTINGS, ...store?.features };
}

/** A store owner's vendor-portal login, as platform staff see and manage it. */
export interface VendorLogin {
  uid: string;
  email: string | null;
  displayName: string;
  lastLoginAt: string | null;
}

/**
 * Sensitive tenant data — legal identity, contact, and bank details used for
 * payouts (NOT payment-gateway credentials; those are per-store Razorpay
 * keys added in a later phase). Stored as `stores/{id}/private/business`,
 * readable only by staff and the owning vendor.
 */
export interface StoreBusinessDetails {
  legalName: string;
  gstin: string | null;
  businessType: LegalBusinessType;
  contactPerson: {
    name: string;
    phone: string;
    email: string;
  };
  address: {
    line1: string;
    line2: string | null;
    city: string;
    state: string;
    pincode: string;
  };
  bankDetails: {
    accountHolder: string;
    accountNumber: string;
    ifsc: string;
    bankName: string;
  };
}

/** The part of a store's branding its vendor (or platform staff) edits from Settings > Branding. */
export interface StoreBrandingSettings {
  logoUrl: string | null;
  tagline: string | null;
  faviconUrl: string | null;
  /** `#rrggbb` — the storefront's buttons, links and accents. */
  primaryColor: string | null;
  seoTitle: string | null;
  seoDescription: string | null;
  /** The coloured strip above the storefront header, e.g. "FREE DELIVERY on all orders"; null hides it. */
  announcementText: string | null;
}

/** Settings > Branding's view: the editable settings plus the store's (read-only) identity. */
export interface StoreBrandingDetails extends StoreBrandingSettings {
  name: string;
  slug: string;
  /** The domain customers are given instead of `{slug}.giftsay.in`; null when there's none. */
  customDomain: string | null;
}

/** What a storefront needs to brand itself — served publicly for the visited domain's store. */
export interface StorefrontBranding extends StoreBrandingSettings {
  storeId: string;
  name: string;
  features: StoreFeatureSettings;
  /** The last status this store moves its orders to — the customer's tracker stops there. */
  finalOrderStatus: FinalOrderStatus;
  /** Which gateway "Pay online" opens for this store. */
  paymentProvider: PaymentProvider;
  /** 'suspended' (set by platform admins) turns the storefront into an "unavailable" notice. */
  status: StoreStatus;
  /** Extra details the checkout asks for (admin-configured). Empty = none. */
  checkoutFields: StoreCheckoutField[];
}

/** Full staff-facing view: public store fields + private business details. */
export interface StoreWithBusinessDetails extends Store {
  businessDetails: StoreBusinessDetails;
}

/** Where a store's order flow ends. Some stores hand parcels to a courier and stop at Shipped;
 *  others track them to the door. */
export type FinalOrderStatus = 'shipped' | 'out_for_delivery' | 'delivered';

export const FINAL_ORDER_STATUSES: readonly FinalOrderStatus[] = [
  'shipped',
  'out_for_delivery',
  'delivered',
];

/** A courier a store ships with, so its tracking link needn't be pasted into every order. */
export interface DeliveryPartner {
  id: string;
  /** e.g. "ST Courier", "Professional Couriers". */
  name: string;
  /** The courier's tracking page. `{trackingNumber}` in it is replaced with the order's
   *  tracking number, e.g. `https://courier.example/track?awb={trackingNumber}`. */
  trackingUrl: string;
}

export interface StoreShippingSettings {
  finalOrderStatus: FinalOrderStatus;
  deliveryPartners: DeliveryPartner[];
}

export const DEFAULT_STORE_SHIPPING_SETTINGS: StoreShippingSettings = {
  finalOrderStatus: 'delivered',
  deliveryPartners: [],
};

export function resolveStoreShipping(
  store: Pick<Store, 'shipping'> | null | undefined,
): StoreShippingSettings {
  return { ...DEFAULT_STORE_SHIPPING_SETTINGS, ...store?.shipping };
}

export const TRACKING_NUMBER_PLACEHOLDER = '{trackingNumber}';

/** A partner's tracking link for one parcel — fills in `{trackingNumber}` when the link has it. */
export function buildTrackingUrl(template: string, trackingNumber: string | null): string {
  return template.split(TRACKING_NUMBER_PLACEHOLDER).join(encodeURIComponent(trackingNumber ?? ''));
}

/** The status steps a store's orders go through, ending at its final status. */
export function orderFlowFor(finalOrderStatus: FinalOrderStatus): OrderStatusValue[] {
  return ORDER_STATUS_FLOW.slice(0, ORDER_STATUS_FLOW.indexOf(finalOrderStatus) + 1);
}
